import "./CardClinicalAction.scss";
import clsx from "clsx";
import { Button, Row } from "@/components";
import { ExternalLink } from "@/icons";
import React, { useMemo, useState } from "react";

type CardClinicalActionProps = {
  title: string;
  subtitle?: string;
  information?: { label: string; value: string | number }[];
  statusLabel?: string;
  statusVariant?: "default" | "warning" | "success" | "error";
  buttonLabel?: string;
  buttonVariant?: "primary" | "secondary";
  onButtonClick?: () => void;
  toggleOnButton?: boolean;
  toggleLabels?: { open: string; close: string };
  isOpen?: boolean;
  deleteLabel?: string;
  onDelete?: () => void;
  deleteButtonPosition?: "left" | "right";
  externalLink?: {
    title: string;
    href: string;
  };
  asLink?: "a" | React.ComponentType<any>;
  className?: string;
  classNameTitle?: string;
};

const statusColors = {
  default: "#6C757D",
  warning: "#DC3545",
  success: "#28A745",
  error: "#DC3545",
};

export const CardClinicalAction = ({
  title,
  subtitle,
  information = [],
  statusLabel,
  statusVariant = "default",
  buttonLabel,
  buttonVariant = "secondary",
  onButtonClick,
  toggleOnButton = false,
  toggleLabels = { open: "Open detail", close: "Close detail" },
  isOpen,
  deleteLabel,
  onDelete,
  deleteButtonPosition = "right",
  externalLink,
  asLink = "a",
  className = "",
  classNameTitle = "fw-semibold fs-6 md-fs-5 text-slate-20",
}: CardClinicalActionProps) => {
  const statusColor = statusColors[statusVariant];
  const LinkComponent = asLink;
  const [internalOpen, setInternalOpen] = useState(false);
  const computedIsOpen = toggleOnButton ? (isOpen ?? internalOpen) : true;
  const currentButtonLabel = useMemo(() => {
    if (!toggleOnButton) return buttonLabel;
    return computedIsOpen ? toggleLabels.close : toggleLabels.open;
  }, [buttonLabel, computedIsOpen, toggleLabels, toggleOnButton]);

  const handleButtonClick = () => {
    if (toggleOnButton) {
      onButtonClick?.();
      setInternalOpen((prev) => !prev);
      return;
    }
    onButtonClick?.();
  };

  return (
    <div
      className={clsx(
        "rounded shadow w-full card-clinical-action-container",
        className,
      )}
    >
      <div className="ui-clinical-action-content p-x24">
        <div className="ui-action-header">
          <div className="clinical-action-info d-flex align-items-center gap-x16">
            <h2
              className={clsx(classNameTitle)}
              title={title}
              style={{ minWidth: "300px", flexShrink: 0 }}
            >
              {title}
            </h2>
            {information.length > 0 && (
              <div className="ui-information-row d-flex gap-x16">
                {information.map((info, index) => (
                  <div key={index} className="ui-info-item">
                    <span className="fs-custom fw-bold text-slate-28">
                      {info.label}:
                    </span>
                    <span className="fs-custom fw-regular text-slate-28">
                      {info.value}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {subtitle && (
            <p
              className={"fs-7 md-fs-6 text-muted fw-regular"}
              title={subtitle}
            >
              {subtitle}
            </p>
          )}
        </div>

        <div className="ui-action-footer">
          {statusLabel && (
            <div
              className={"fs-7 fw-bold text-white rounded py-x4 px-x16"}
              style={{
                width: "max-content",
                backgroundColor: statusColor,
              }}
              role="status"
              aria-label={`Stato: ${statusLabel}`}
            >
              {statusLabel}
            </div>
          )}
          {externalLink ? (
            <LinkComponent
              href={externalLink.href}
              target="_blank"
              rel="noopener noreferrer"
              className="d-flex align-items-center gap-x8 text-decoration-none text-primary fw-bold fs-6"
              aria-label={`${externalLink.title} (si apre in una nuova finestra)`}
            >
              {externalLink.title}
              <ExternalLink
                width={20}
                height={20}
                color="primary"
                aria-hidden="true"
              />
            </LinkComponent>
          ) : (
            currentButtonLabel && (
              <Button
                variant={buttonVariant}
                size={"3"}
                onClick={handleButtonClick}
                aria-label={`${currentButtonLabel} ${title}`}
              >
                {currentButtonLabel}
              </Button>
            )
          )}
        </div>
      </div>
      {computedIsOpen && deleteLabel && (
        <Row
          className={clsx(
            "align-items-center px-x24 w-full bg-lightblue-a03 py-x16",
            deleteButtonPosition === "right"
              ? "justify-content-end"
              : "justify-content-start",
          )}
        >
          <Button
            variant={"secondary"}
            size={"3"}
            variantBg={"bg-lightblue-a03"}
            onClick={onDelete}
          >
            {deleteLabel}
          </Button>
        </Row>
      )}
    </div>
  );
};
